// src/services/api/base.js
import axios from 'axios';
import { verifyToken } from './auth';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 180000, // ✅ 3 minutes for bulk operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 unauthorized errors
    if (error.response?.status === 401 && !originalRequest?.url?.includes('/auth/login')) {
      // Clear stored token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 403 subscription errors
    if (error.response?.status === 403) {
      const errorData = error.response?.data;
      
      // Check if this is a subscription-related error
      if (errorData?.subscriptionRequired || errorData?.code?.includes('SUBSCRIPTION')) {
        try {
          // Refresh user data to get latest subscription status
          const response = await verifyToken();
          
          // Update stored user data
          if (response.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
            
            // Dispatch custom event to notify app of subscription status change
            window.dispatchEvent(new CustomEvent('subscriptionStatusChanged', {
              detail: {
                hasActiveSubscription: response.user.hasActiveSubscription,
                status: response.user.subscription?.status
              }
            }));
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
        }
        
        // Don't retry the original request, let the component handle it
        return Promise.reject(error);
      }
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;